import { Autocomplete, Box, Grid, TextField } from '@mui/material'
import { FC, useState, useReducer, useEffect } from 'react'
import { Vendor } from '../../../services'
import { AppDialog } from '../../../components'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  addVendorAction,
  addVendorResetAction,
  fetchVendorsAction,
} from '../../../store/vendors'
import { showAlertAction } from '../../../store/alerts'
import {
  isValidMobileNumber,
  indianMobileNumberRegex,
  isValidGST,
  setDefaultGSTType,
  getDefaultGstType,
} from '../../../utils'
import SelectStateItem, { StateOption, getState } from '../Customers/SelectStateItem'
import { fetchGSTINAction } from '../../../store/gstr'
import GSTINDetailsItemV2 from '../../../components/GSTINDetailsItemV2'

type AddVendorFields = 'name' | 'mobile' | 'address' | 'address2' | 'gstin' | 'pan' | 'place' | 'pincode'
type AddVendorError = Record<AddVendorFields, Record<string, string>>
const addVendorErrors: AddVendorError = {
  name: { empty: '' },
  mobile: { empty: '' },
  address: { empty: '' },
  address2: { empty: '' },
  gstin: { empty: '' },
  pan: { empty: '' },
  place: { empty: '' },
  pincode: { empty: 'Pincode must be exactly 6 digits' },
}

type InputBox = {
  value: string
  error: boolean
  helperText: string
}

type AddVendorFieldsState = Record<AddVendorFields, InputBox>

const defaultObj: InputBox = {
  value: '',
  error: false,
  helperText: '',
}

const initialAddVendorState: AddVendorFieldsState = {
  name: { ...defaultObj },
  mobile: { ...defaultObj },
  address: { ...defaultObj },
  address2: { ...defaultObj },
  gstin: { ...defaultObj },
  pan: { ...defaultObj },
  place: { ...defaultObj },
  pincode: { ...defaultObj },
}

const addVendorReducer = (
  state: AddVendorFieldsState,
  action: { type: string; payload: { name: AddVendorFields; value: string } }
) => {
  const {
    type,
    payload: { name, value },
  } = action
  switch (type) {
    case 'SET':
      return {
        ...state,
        [name]: { ...state[name], error: false, value, helperText: '' },
      }
    case 'ERROR': {
      const nameObj = addVendorErrors[name]
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value,
          helperText: nameObj ? nameObj['empty'] : '',
        },
      }
    }
    default:
      return state
  }
}

export interface AddVendorProps {
  children?: React.ReactNode
  vendor?: Vendor
  onClose?: (vendor?: Vendor) => void
}

interface GSTRegType {
  label: string
  index: number
  value: string
}

const gstTypes: GSTRegType[] = [
  { label: 'Registered', index: 0, value: 'REGULAR' },
  { label: 'Unregistered (URP)', index: 1, value: 'URG_CONSUMER' },
]

function getRegType(regType?: string): GSTRegType {
  const found = gstTypes.find((t) => t.value === regType)
  return found || gstTypes[0]
}

const AddVendor: FC<AddVendorProps> = ({ onClose, vendor }) => {
  const [stateOption, setStateOption] = useState<StateOption | null>(null)
  const [gstRegType, setGstRegType] = useState<GSTRegType>(gstTypes[0])
  const {
    data,
    success: addVendorSuccess,
    message,
  } = useAppSelector((state) => state.addVendor)

  const dispatchAction = useAppDispatch()
  const [addVendorData, dispatch] = useReducer(
    addVendorReducer,
    initialAddVendorState
  )

  const { name, mobile, address, address2, gstin, pan, place, pincode } = addVendorData
  const [isClose, setClose] = useState<boolean>(false)
  const [stateError, setStateError] = useState<boolean>(false)
  const [gstinError, setGstinError] = useState<boolean>(false)

  useEffect(() => {
    const index = Number(getDefaultGstType())
    const savedType = gstTypes[index]
    if (savedType) {
      setGstRegType(savedType)
    }
  }, [])

  const onChangeHandler = (e: any) => {
    const {
      target: { name: fieldName, value },
    } = e
    if (value.length > 0 || fieldName === 'mobile' || fieldName === 'address2' || fieldName === 'place' || fieldName === 'pincode') {
      dispatch({ type: 'SET', payload: { name: fieldName, value } })
    } else {
      dispatch({ type: 'ERROR', payload: { name: fieldName, value } })
    }
    if (fieldName === 'gstin' && value.length === 15) {
      dispatchAction(fetchGSTINAction(value))
    }
  }

  const submitHandle = () => {
    let error = false
    if (!name?.value) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'name', value: '' } })
    }
    const mobileTrimmed = (mobile?.value ?? '').trim()
    if (mobileTrimmed && !isValidMobileNumber(mobileTrimmed)) {
      error = true
      dispatch({
        type: 'ERROR',
        payload: { name: 'mobile', value: mobile.value },
      })
    }
    if (!gstRegType) error = true
    const gstinVisible = !(
      gstRegType?.value === 'URG_BUSINESS' ||
      gstRegType?.value === 'URG_CONSUMER' ||
      gstRegType?.value === 'OVERSEAS'
    )
    if (gstinVisible && (!gstin?.value || !isValidGST(gstin?.value))) {
      error = true
      setGstinError(true)
      dispatch({ type: 'ERROR', payload: { name: 'gstin', value: gstin?.value || '' } })
    }
    if (!address?.value) {
      error = true
      dispatch({ type: 'ERROR', payload: { name: 'address', value: '' } })
    }
    const pincodeValue = (pincode?.value ?? '').trim()
    if (pincodeValue && (pincodeValue.length !== 6 || isNaN(Number(pincodeValue)))) {
      error = true
      dispatch({
        type: 'ERROR',
        payload: { name: 'pincode', value: pincode.value },
      })
    }
    if (gstinVisible && !stateOption) {
      error = true
      setStateError(true)
    }

    if (!error) {
      dispatchAction(
        addVendorAction({
          ...(vendor?.id && { id: vendor.id }),
          name: name.value,
          mobile: mobileTrimmed,
          address: address.value,
          address2: address2.value || undefined,
          gstin: gstinVisible ? gstin.value : '',
          stateCode: stateOption?.code,
          state: stateOption?.label,
          pan: pan.value,
          place: place.value || undefined,
          pincode: pincode.value || undefined,
          profileUrl: null,
          registrationType: gstRegType.value,
        })
      )
    }
  }

  useEffect(() => {
    if (vendor) {
      if (!vendor.registrationType && !vendor.gstin) {
        setGstRegType(gstTypes[1])
      } else {
        setGstRegType(getRegType(vendor.registrationType))
      }
      dispatch({ type: 'SET', payload: { name: 'mobile', value: vendor.mobile } })
      dispatch({ type: 'SET', payload: { name: 'name', value: vendor.name } })
      dispatch({ type: 'SET', payload: { name: 'address', value: vendor.address } })
      dispatch({ type: 'SET', payload: { name: 'address2', value: vendor.address2 || '' } })
      dispatch({ type: 'SET', payload: { name: 'gstin', value: vendor.gstin || '' } })
      dispatch({ type: 'SET', payload: { name: 'pan', value: vendor.pan || '' } })
      dispatch({ type: 'SET', payload: { name: 'place', value: vendor.place || '' } })
      dispatch({ type: 'SET', payload: { name: 'pincode', value: vendor.pincode || '' } })
      if (vendor.state && vendor.stateCode) {
        setStateOption({ code: vendor.stateCode, label: vendor.state })
      }
    }
  }, [vendor])

  useEffect(() => {
    if (addVendorSuccess) {
      dispatchAction(fetchVendorsAction())
      dispatchAction(addVendorResetAction())
      dispatchAction(
        showAlertAction({ message: 'Done successfully', severity: 'success' })
      )
      onClose?.(data || undefined)
    } else if (message) {
      dispatchAction(showAlertAction({ message, severity: 'error' }))
      dispatchAction(addVendorResetAction())
    }
  }, [addVendorSuccess, message, dispatchAction, onClose, data])

  const onSelectChange = (_e: any, newValue: GSTRegType | null) => {
    if (newValue) {
      setDefaultGSTType(String(newValue.index))
      setGstRegType(newValue)
    }
  }

  return (
    <AppDialog
      id="add-vendor"
      title={!vendor ? 'Add Vendor' : 'Edit Vendor'}
      submitButtonText="SUBMIT"
      onClose={onClose}
      close={isClose}
      onSubmit={submitHandle}
      maxWidth="sm"
    >
      <Box
        component="form"
        sx={{
          '& .MuiTextField-root': { m: 1, width: '25ch' },
        }}
        noValidate
        autoComplete="off"
      >
        <Grid container>
          <Grid item xs={11.6}>
            <Autocomplete
              size="small"
              disablePortal
              id="gstin_type_options_vendor"
              value={gstRegType}
              options={gstTypes}
              onChange={onSelectChange}
              renderInput={(params) => (
                <TextField
                  size="small"
                  {...params}
                  label="Select GST Registration Type"
                  required
                  error={!gstRegType}
                  sx={{ minWidth: '100%', margin: 0 }}
                />
              )}
              renderOption={(props, option) => (
                <Grid component="li" container {...props}>
                  <Grid item xs pl={1} pr={1} pt={0.7} pb={0.7}>
                    {option.label}
                  </Grid>
                </Grid>
              )}
            />
          </Grid>
          {!(
            gstRegType?.value === 'URG_BUSINESS' ||
            gstRegType?.value === 'URG_CONSUMER' ||
            gstRegType?.value === 'OVERSEAS'
          ) && (
            <>
              <Grid item xs={12}>
                <GSTINDetailsItemV2
                  minWidth={545}
                  gstinError={gstinError}
                  initialGstin={gstin.value}
                  required
                  onUpdate={(gstinVal, gstinObj) => {
                    setGstinError(false)
                    dispatch({
                      type: 'SET',
                      payload: { name: 'gstin', value: gstinVal },
                    })
                    if (gstinVal && gstinVal.length > 2 && !stateOption) {
                      setStateOption(getState(gstinVal.substring(0, 2)))
                    }
                    if (
                      gstinVal &&
                      gstinVal.length > 11 &&
                      (!pan.value || pan.value.length === 0)
                    ) {
                      dispatch({
                        type: 'SET',
                        payload: { name: 'pan', value: gstinVal.substring(2, 12) },
                      })
                    }
                    if (gstinObj?.tradeName) {
                      dispatch({
                        type: 'SET',
                        payload: { name: 'name', value: gstinObj.tradeName },
                      })
                      dispatch({
                        type: 'SET',
                        payload: {
                          name: 'address',
                          value: gstinObj.address1 || '',
                        },
                      })
                      dispatch({
                        type: 'SET',
                        payload: {
                          name: 'address2',
                          value: gstinObj.address2 || '',
                        },
                      })
                      if (gstinObj.place) {
                        dispatch({
                          type: 'SET',
                          payload: { name: 'place', value: gstinObj.place },
                        })
                      }
                      if (gstinObj.pinCode) {
                        dispatch({
                          type: 'SET',
                          payload: { name: 'pincode', value: gstinObj.pinCode },
                        })
                      }
                      setStateOption(getState(gstinObj.stateCode))
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="pan"
                  value={pan.value}
                  label="PAN Number"
                  error={!!(pan.value && pan.error)}
                  helperText={pan.helperText}
                  onChange={onChangeHandler}
                  sx={{ minWidth: 545 }}
                  InputLabelProps={{ shrink: !!pan.value }}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <TextField
              size="small"
              name="name"
              value={name.value}
              label="Name"
              error={name.error}
              required
              helperText={name.helperText}
              onChange={onChangeHandler}
              sx={{ minWidth: 545 }}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <TextField
              size="small"
              name="address"
              value={address.value}
              label="Address"
              error={address.error}
              required
              helperText={address.helperText}
              onChange={onChangeHandler}
              sx={{ minWidth: 545 }}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <TextField
              size="small"
              name="address2"
              value={address2.value}
              label="Address Line 2"
              error={address2.error}
              helperText={address2.helperText}
              onChange={onChangeHandler}
              sx={{ minWidth: 545 }}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <Box sx={{ display: 'flex', gap: 1, minWidth: 545 }}>
              <TextField
                size="small"
                name="place"
                value={place.value}
                label="Place (Optional)"
                onChange={onChangeHandler}
                sx={{ flex: 1, m: 0 }}
              />
              <TextField
                size="small"
                name="pincode"
                value={pincode.value}
                label="Pincode (Optional)"
                error={pincode.error}
                helperText={pincode.error ? pincode.helperText : ''}
                onChange={onChangeHandler}
                inputProps={{ maxLength: 6 }}
                sx={{ width: 160, m: 0 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} mt={1}>
            <SelectStateItem
              error={stateError}
              minWidth={545}
              selectedStateOption={stateOption}
              onChange={(option) => {
                setStateError(false)
                setStateOption(option)
              }}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <TextField
              size="small"
              name="mobile"
              value={mobile.value}
              label="Mobile (Optional)"
              type="string"
              error={mobile.error}
              helperText={mobile.helperText}
              onChange={onChangeHandler}
              fullWidth
              inputProps={{ maxLength: 13, pattern: indianMobileNumberRegex }}
              sx={{ minWidth: 545 }}
            />
          </Grid>
        </Grid>
      </Box>
    </AppDialog>
  )
}

export default AddVendor
